import { dashboardApi, jobsApi } from '@/api/jobs';
import { projectsApi } from '@/api/projects';
import { inventoryApi } from '@/api/inventory';
import { warehouseApi } from '@/api/warehouse';
import type { DashboardData, InventoryItem, Category, Job, Project } from '@/types';

/**
 * Shared query definitions (cache key + fetcher) for page-level data.
 *
 * These exist in one place because two callers must agree on them exactly:
 * the page that renders the data (via usePageData) and the post-login
 * prefetcher (usePrefetchAppData) that warms the cache in the background.
 * If a page fetched under a different key than the prefetcher warmed, the
 * prefetch would be wasted — so neither side defines keys locally.
 */

export const dashboardQuery = {
  key: ['dashboard'] as const,
  fetch: (): Promise<DashboardData> => dashboardApi.get(),
};

export const jobsQuery = {
  key: ['jobs'] as const,
  fetch: (): Promise<Job[]> => jobsApi.list(),
};

export const projectsQuery = {
  key: ['projects'] as const,
  fetch: (): Promise<Project[]> => projectsApi.list(),
};

export const inventoryListQuery = {
  key: ['inventory-list'] as const,
  fetch: async (): Promise<{ items: InventoryItem[]; categories: Category[] }> => {
    const [items, categories] = await Promise.all([inventoryApi.list(), inventoryApi.listCategories()]);
    return { items, categories };
  },
};

export const warehouseQuery = {
  key: ['warehouse'] as const,
  fetch: async () => {
    const [jobs, overdue, outstanding] = await Promise.all([
      warehouseApi.listJobs(),
      warehouseApi.overdue(),
      warehouseApi.outstanding(),
    ]);
    return { jobs, overdue: overdue as any[], outstanding: outstanding as any[] };
  },
};

// ── Calendar ─────────────────────────────────────────────────────────────────

/** Monday-anchored week start — the week cache key derives from this. */
export function startOfWeek(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  let dow = out.getDay() - 1; // Mon=0..Sun=6
  if (dow < 0) dow = 6;
  out.setDate(out.getDate() - dow);
  return out;
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

export const calendarMonthQuery = (year: number, month: number) => ({
  key: ['calendar-month', year, month] as const,
  fetch: async (): Promise<Job[]> => {
    const data: any = await dashboardApi.calendar(year, month).catch(() => ({ jobs: [] }));
    // Filter out jobs whose quote is declined
    return ((data.jobs ?? []) as any[]).filter((j) => j.quote?.status !== 'declined') as Job[];
  },
});

export const calendarWeekQuery = (weekStart: Date) => ({
  key: ['calendar-week', weekStart.toISOString().slice(0, 10)] as const,
  fetch: async (): Promise<Job[]> => {
    // The visible week may span two months — fetch both to be safe
    const weekEnd = addDays(weekStart, 6);
    const monthsToFetch: Array<{ year: number; month: number }> = [
      { year: weekStart.getFullYear(), month: weekStart.getMonth() + 1 },
    ];
    if (weekEnd.getMonth() !== weekStart.getMonth()) {
      monthsToFetch.push({ year: weekEnd.getFullYear(), month: weekEnd.getMonth() + 1 });
    }
    const results = await Promise.all(
      monthsToFetch.map((m) => dashboardApi.calendar(m.year, m.month).catch(() => ({ jobs: [] } as any))),
    );
    const seen = new Set<string>();
    const all: Job[] = [];
    for (const r of results as any[]) {
      for (const j of (r.jobs ?? [])) {
        if (j.quote?.status === 'declined') continue;
        if (seen.has(j.id)) continue;
        seen.add(j.id);
        all.push(j);
      }
    }
    return all;
  },
});
